import { AxiosError } from "axios";
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getColorAnalysisData } from "../../../app/api/colorAnalysis";
import { RootState } from "../../../app/store";
import { SIDEBAR_GRAPH_WIDTH } from "../../../constants/constants";
import { DataNode } from "../../../interface/Trends";
import { showChart } from "../../../utils/trendSidebarGraphUtils/colorAnalysisUtils";
import { getTrendFiltersForGraphs } from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import CollapseComponent from "../TrendsSnapshot/CollapseComponent";
import ColorAnalysisTable from "./ColorAnalysisTable";

type ColorAnalysisProps = {
  width?: number;
  data: DataNode;
  colorForLevel2?: string;
  setColorForLevel2?: React.Dispatch<React.SetStateAction<string>>;
  colorForLevel3?: string;
  setColorForLevel3?: React.Dispatch<React.SetStateAction<string>>;
  dataForLevel2: DataNode | null;
  setDataForLevel2: React.Dispatch<React.SetStateAction<DataNode | null>>;
  dataForLevel3: DataNode | null;
  setDataForLevel3: React.Dispatch<React.SetStateAction<DataNode | null>>;
  trendFilterType: string;
  expandedView?: boolean;
};

const ColorAnalysisGraph: React.FC<ColorAnalysisProps> = ({
  width = SIDEBAR_GRAPH_WIDTH,
  data,
  colorForLevel2,
  setColorForLevel2,
  colorForLevel3,
  setColorForLevel3,
  dataForLevel2,
  setDataForLevel2,
  dataForLevel3,
  setDataForLevel3,
  trendFilterType,
  expandedView = false,
}) => {
  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);
  const [l2Loading, setL2Loading] = useState(false);

  const user = useSelector((state: RootState) => state.user.userInfo);

  const [l1Open, setL1Open] = useState(true);
  const [l2Open, setL2Open] = useState(true);
  const [l3Open, setL3Open] = useState(true);

  const toggleL1Open = () => setL1Open(prev => !prev);
  const toggleL2Open = () => setL2Open(prev => !prev);
  const toggleL3Open = () => setL3Open(prev => !prev);

  const collapseLevel3 = () => {
    if (setColorForLevel3) setColorForLevel3("");
    setL3Open(false);
    setDataForLevel3(null);
  };

  const collapseLevel2 = () => {
    collapseLevel3();
    if (setColorForLevel2) setColorForLevel2("");
    setL2Open(false);
    setDataForLevel2(null);
  };

  useEffect(() => {
    setColorForLevel2 && setColorForLevel2("");
    setColorForLevel3 && setColorForLevel3("");
    setDataForLevel2(null);
    setDataForLevel3(null);
  }, [trendFilterType]);

  const fetchL2ColorAnalysisData = async (primary_colour: string) => {
    try {
      setL2Loading(true);
      const trendFilters = getTrendFiltersForGraphs();
      const response = await getColorAnalysisData(
        { ...trendFilters, primary_colour },
        user
      );
      if (response?.data && chartRef2.current) {
        const data: DataNode = response?.data?.children;
        setDataForLevel2(data);
        showChart(
          data,
          chartRef2.current,
          "LEVEL2",
          width,
          expandedView,
          setColorForLevel3
        );
        // Check if previous l3 data exists
        const l3Data = data.children?.find(d => d.color === colorForLevel3);
        if (!l3Data && setColorForLevel3) {
          setColorForLevel3("");
        } else if (l3Data && chartRef3.current) {
          setDataForLevel3(l3Data);
          showChart(l3Data, chartRef3.current, "LEVEL3", width, expandedView);
          setL2Open(true);
        }
        setL3Open(true);
      }
    } catch (error) {
      console.error("Error Fetching level 2 data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error Fetching level 2 data");
      }
    } finally {
      setL2Loading(false);
    }
  };

  useEffect(() => {
    if (chartRef1.current) {
      showChart(
        data,
        chartRef1.current,
        "LEVEL1",
        width,
        expandedView,
        setColorForLevel2
      );
    }
  }, []);

  useEffect(() => {
    setDataForLevel2(null);
    if (setColorForLevel3) {
      if (chartRef2.current && colorForLevel2) {
        fetchL2ColorAnalysisData(colorForLevel2?.split("#")[1]);
        chartRef2.current.scrollIntoView({ behavior: "smooth" });
        setL2Open(true);
      }
    }
  }, [colorForLevel2]);

  useEffect(() => {
    setDataForLevel3(null);
    if (chartRef3.current && colorForLevel3 && dataForLevel2) {
      const level3Data = dataForLevel2.children?.find(
        d => d.color === colorForLevel3
      );
      if (level3Data) {
        setDataForLevel3(level3Data);
        showChart(level3Data, chartRef3.current, "LEVEL3", width, expandedView);
        chartRef3.current?.scrollIntoView({ behavior: "smooth" });
        setL3Open(true);
      }
    }
  }, [colorForLevel3]);

  return (
    <div className="space-y-4">
      {!expandedView && data && (
        <CollapseComponent
          open={l1Open}
          toggle={toggleL1Open}
          text="L1 Analysis"
          snapshot={false}
          expandedView={true}
        />
      )}
      <div
        ref={chartRef1}
        id="LEVEL1"
        className={` ${!l1Open ? "hidden" : "block"}`}
      />
      {expandedView && <ColorAnalysisTable data1={data} />}
      {!expandedView && dataForLevel2 && (
        <CollapseComponent
          open={l2Open}
          toggle={toggleL2Open}
          text="L2 Analysis"
          snapshot={false}
          onClear={collapseLevel2}
        />
      )}
      <div
        className={`${l2Loading || !l2Open || !dataForLevel2 ? "hidden" : "block"} relative`}
      >
        <div ref={chartRef2} id="LEVEL2" />
        <button
          className={`text-red-700 absolute top-0 right-0 mr-4 mt-4 text-lg ${
            expandedView ? "block" : "hidden"
          }`}
          onClick={collapseLevel2}
        >
          Clear
        </button>
      </div>
      <div
        className={`text-3xl font-bold py-20 text-center ${l2Loading ? "block" : "hidden"}`}
      >
        <Skeleton height={width} width={width} circle={true} />
      </div>
      {expandedView && <ColorAnalysisTable data2={dataForLevel2} />}
      {!expandedView && colorForLevel3 && (
        <CollapseComponent
          open={l3Open}
          toggle={toggleL3Open}
          text="L3 Analysis"
          snapshot={false}
          onClear={collapseLevel3}
        />
      )}
      {colorForLevel3 && (
        <div className={`${!l3Open ? "hidden" : "block"} relative`}>
          <button
            className={`text-red-700 absolute top-0 right-0 mr-4 mt-4 text-lg ${
              expandedView ? "block" : "hidden"
            }`}
            onClick={collapseLevel3}
          >
            Clear
          </button>
          <div ref={chartRef3} id="LEVEL3" />
        </div>
      )}
      {expandedView && <ColorAnalysisTable data3={dataForLevel3} />}
    </div>
  );
};

export default ColorAnalysisGraph;
